import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import { toast } from "sonner";

import { setDeviceId } from "@/lib/apis/device-id";
import { createClient } from "@/lib/supabase/component";

const COOKIE_NAME = "vioux_device_id";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 ano

/** Touch inicial sai do caminho crítico do first paint (dispara pós-idle). */
export const TOUCH_IDLE_DELAY_MS = 2_000;
/** Throttle no CLIENTE: evita 1 request por navegação (o server já throttla o UPDATE, mas a chamada em si custava um roundtrip por página). */
export const TOUCH_MIN_INTERVAL_MS = 60_000;

export function FingerprintInit() {
  const router = useRouter();
  const { data, error } = useVisitorData(
    { extendedResult: true },
    { immediate: true }
  );

  /**
   * Touch + BLOCK enforcement — ÚNICO caller de /api/devices/touch no app
   * (antes duplicado com o UserProvider). Se o backend responder BLOCK,
   * desloga e manda pro login. Fail-open em erro de rede.
   */
  const touchDevice = async (deviceId: string) => {
    try {
      const res = await fetch("/api/devices/touch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: deviceId }),
        credentials: "same-origin",
      });
      const body = await res.json().catch(() => ({}));

      if (body.status === "BLOCK") {
        const supabase = createClient();
        await supabase.auth.signOut();
        toast.error(
          "Acesso negado. Este dispositivo foi bloqueado. Contate o administrador."
        );
        router.replace("/login?reason=device_blocked");
      }
    } catch {
      // fail-open: nao quebra navegacao se a sync falhar
    }
  };

  const lastTouchAtRef = useRef(0);

  const throttledTouch = (visitorId: string) => {
    const now = Date.now();
    if (now - lastTouchAtRef.current < TOUCH_MIN_INTERVAL_MS) return;
    lastTouchAtRef.current = now;
    touchDevice(visitorId);
  };

  useEffect(() => {
    if (data?.visitorId) {
      setDeviceId(data.visitorId);
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(data.visitorId)}; path=/; SameSite=Lax; max-age=${COOKIE_MAX_AGE}`;

      // Heartbeat inicial em IDLE — não compete com o first paint da página.
      const t = setTimeout(() => throttledTouch(data.visitorId), TOUCH_IDLE_DELAY_MS);
      return () => clearTimeout(t);
    }
    if (error) {
      console.warn("FingerprintJS failed to load:", error.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.visitorId, error]);

  // Heartbeat em navegacao client-side, com throttle de 1min no cliente
  // (antes: 1 request por navegação; o server só throttlava o UPDATE).
  useEffect(() => {
    if (!data?.visitorId) return;
    const visitorId = data.visitorId;

    const handleRouteChange = () => throttledTouch(visitorId);
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.visitorId, router.events]);

  return null;
}
